import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, KeyboardAvoidingView, Platform, ScrollView } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuth } from '../src/context/AuthContext';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import { useToast } from '../src/components/Toast';
import Button from '../src/components/Button';
import ContentContainer from '../src/components/ContentContainer';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';
import { api, saveToken, saveRefreshToken } from '../src/api/client';

/** VerifyEmailScreen - 6-digit OTP input for email verification after registration. */
export default function VerifyEmailScreen() {
  const { email, returnTo } = useLocalSearchParams<{ email: string; returnTo?: string }>();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  // The address codes are sent to; updated in place when the user fixes a typo'd email.
  const [currentEmail, setCurrentEmail] = useState(email || '');
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);
  const refs = useRef<(TextInput | null)[]>([]);
  const router = useRouter();
  const { completeVerification, isAuthenticated } = useAuth();
  const toast = useToast();
  const { colors } = useTheme();
  const styles = useStyles(colors);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /** Process digit input; supports full-code paste across all 6 fields. */
  const handleDigitChange = (text: string, index: number) => {
    const clean = text.replace(/[^0-9]/g, '');
    if (clean.length > 1) {
      // Handle paste of full code
      const pasted = clean.slice(0, 6).split('');
      const newDigits = [...digits];
      pasted.forEach((d, i) => { if (i + index < 6) newDigits[i + index] = d; });
      setDigits(newDigits);
      const nextIdx = Math.min(index + pasted.length, 5);
      refs.current[nextIdx]?.focus();
      return;
    }
    const newDigits = [...digits];
    newDigits[index] = clean;
    setDigits(newDigits);
    if (clean && index < 5) refs.current[index + 1]?.focus();
  };

  /** Move focus to previous field on backspace when current field is empty. */
  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  /** POST /api/auth/verify-email — validate OTP and complete auth flow. */
  const handleVerify = async () => {
    const code = digits.join('');
    if (code.length !== 6) { setError('Please enter the full 6-digit code'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api<{ token: string; refreshToken: string; role: string; profile: any }>('/api/auth/verify-email', {
        method: 'POST',
        body: { email: currentEmail, code },
        skipAuth: true,
      });
      await completeVerification(res.token, res.refreshToken, res.role as 'owner' | 'user' | 'admin', res.profile);
      router.replace((returnTo || '/') as any);
    } catch (err: any) {
      setError(err.message || 'Invalid code');
    } finally { setLoading(false); }
  };

  /** PUT /api/auth/profile — fix a typo'd/wrong signup email; backend re-sends
   *  the code to the new address. Requires the soft-gate session. */
  const handleChangeEmail = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) { setError('Please enter a valid email address'); return; }
    setError('');
    setChangingEmail(true);
    try {
      const res = await api<{ success: boolean; token: string; emailChanged: boolean }>('/api/auth/profile', {
        method: 'PUT',
        body: { email: trimmed },
      });
      if (res.token) await saveToken(res.token);
      setCurrentEmail(trimmed);
      setShowChangeEmail(false);
      setNewEmail('');
      setDigits(['', '', '', '', '', '']);
      setCooldown(60);
      toast.show({ type: 'success', title: 'Email updated', message: `A new code was sent to ${trimmed}` });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Could not change email', message: err.message });
    } finally { setChangingEmail(false); }
  };

  /** POST /api/auth/resend-code — resend verification email with 60s cooldown. */
  const handleResend = async () => {
    if (cooldown > 0) return;
    try {
      await api('/api/auth/resend-code', { method: 'POST', body: { email: currentEmail, type: 'verification' }, skipAuth: true });
      const RESEND_COOLDOWN_SECONDS = 60;
      setCooldown(RESEND_COOLDOWN_SECONDS);
      toast.show({ type: 'success', title: 'Code resent!' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <ContentContainer maxWidth={480}>
          <Text style={styles.title}>Verify Your Email</Text>
          <Text style={styles.subtitle}>Enter the 6-digit code sent to</Text>
          <Text style={styles.email}>{currentEmail}</Text>

          <View style={styles.otpRow}>
            {digits.map((d, i) => (
              <TextInput
                key={i}
                ref={r => { refs.current[i] = r; }}
                style={[styles.otpInput, d ? styles.otpInputFilled : null]}
                value={d}
                onChangeText={text => handleDigitChange(text, i)}
                onKeyPress={e => handleKeyPress(e, i)}
                keyboardType="number-pad"
                maxLength={6}
                selectTextOnFocus
                autoFocus={i === 0}
              />
            ))}
          </View>

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <Button title="Verify Email" onPress={handleVerify} loading={loading} size="lg" style={styles.verifyBtn} />

          <TouchableOpacity onPress={handleResend} disabled={cooldown > 0} style={styles.resendContainer}>
            <Text style={[styles.resendText, cooldown > 0 && { color: colors.textLight }]}>
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </Text>
          </TouchableOpacity>

          {/* Typo'd/fake signup email: authenticated (soft-gate) users can fix it
              in place — the backend re-sends the code to the new address. */}
          {isAuthenticated && !showChangeEmail ? (
            <TouchableOpacity onPress={() => setShowChangeEmail(true)} style={styles.changeEmailContainer}>
              <Text style={styles.resendText}>Wrong email? Change it</Text>
            </TouchableOpacity>
          ) : null}
          {isAuthenticated && showChangeEmail ? (
            <View style={styles.changeEmailBox}>
              <TextInput
                style={styles.changeEmailInput}
                value={newEmail}
                onChangeText={setNewEmail}
                placeholder="Correct email address"
                placeholderTextColor={colors.textLight}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoFocus
              />
              <Button title="Send code to this email" onPress={handleChangeEmail} loading={changingEmail} size="md" />
              <TouchableOpacity onPress={() => { setShowChangeEmail(false); setNewEmail(''); }} style={styles.skipContainer}>
                <Text style={styles.skipText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {/* Soft gate: a customer who already has a session can pay now and verify later. */}
          {isAuthenticated ? (
            <TouchableOpacity onPress={() => router.replace((returnTo || '/') as any)} style={styles.skipContainer}>
              <Text style={styles.skipText}>Skip for now</Text>
            </TouchableOpacity>
          ) : null}
        </ContentContainer>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { flexGrow: 1, padding: spacing.lg, paddingTop: spacing.xl },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs, textAlign: 'center' },
  subtitle: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center' },
  email: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.primary, textAlign: 'center', marginBottom: spacing.lg },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.md, textAlign: 'center' },
  otpRow: { flexDirection: 'row', justifyContent: 'center', gap: 8, marginBottom: spacing.lg },
  otpInput: {
    width: 48, height: 56, borderRadius: borderRadius.md,
    borderWidth: 2, borderColor: c.borderLight,
    backgroundColor: c.surface, textAlign: 'center',
    fontSize: 24, fontWeight: fontWeight.bold, color: c.text,
  },
  otpInputFilled: { borderColor: c.primary },
  verifyBtn: { marginTop: spacing.sm },
  resendContainer: { alignItems: 'center', marginTop: spacing.lg },
  resendText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  changeEmailContainer: { alignItems: 'center', marginTop: spacing.md },
  changeEmailBox: { marginTop: spacing.md, gap: spacing.sm },
  changeEmailInput: {
    borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.md,
    backgroundColor: c.surface, color: c.text,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm, fontSize: fontSize.md,
  },
  skipContainer: { alignItems: 'center', marginTop: spacing.md },
  skipText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
